import { blocksScore, buildBlocks, type LibField } from '../components/FormModal.js';

/**
 * ehr#253 - the print / PDF of a filled form, laid out like the clinician's View: rating
 * questions as one grid (the scale across the top, the chosen answer filled in green),
 * headings, typed answers in boxes, and the score. The browser's Print dialog saves it as a
 * PDF. Pure, so it is unit-tested.
 */

const esc = (v: unknown) =>
  String(v ?? '').replace(
    /[&<>"]/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] ?? c,
  );

const STYLES = `
*{box-sizing:border-box}
body{font-family:'Sora',system-ui,sans-serif;color:#0d1f18;padding:36px;max-width:820px;margin:0 auto;
  -webkit-print-color-adjust:exact;print-color-adjust:exact}
h1{font-size:22px;margin:0 0 4px}
.meta{font-size:12px;color:#527060;margin-bottom:6px}
.desc{font-size:12px;color:#527060;margin-bottom:20px}
h2{font-size:13.5px;margin:22px 0 10px}
table{width:100%;border-collapse:collapse;border:1px solid #dfe8e3;border-radius:8px;font-size:12.5px;margin-bottom:16px}
th{font-size:10px;font-weight:700;letter-spacing:.05em;text-transform:uppercase;color:#527060;
  background:#f4f7f5;padding:9px 6px;text-align:center;border-bottom:1px solid #dfe8e3}
th.item{text-align:left}
td{padding:9px 8px;border-bottom:1px solid #edf2ef;text-align:center;vertical-align:middle}
td.item{text-align:left}
tr:nth-child(even) td{background:#fafcfb}
td.on{background:#d4f4e8 !important}
.dot{display:inline-block;width:14px;height:14px;border-radius:50%;border:1.5px solid #c9d6cf}
.dot.on{border-color:#1a8a66;background:radial-gradient(circle,#1a8a66 0 45%,#fff 50%)}
.fld{margin:0 0 12px}
.lbl{font-size:12px;font-weight:600;color:#3d5a4c;margin-bottom:4px}
.val{font-size:13px;font-weight:600;white-space:pre-wrap;background:#edfaf4;border:1px solid #d4f4e8;
  border-radius:6px;padding:8px 10px;min-height:34px}
.val.empty{font-weight:400;color:#8aa596;background:#fff;border-color:#e3ebe7}
.score{display:flex;justify-content:space-between;align-items:center;border:1px solid #d4f4e8;
  background:#edfaf4;border-radius:8px;padding:12px 16px;margin-top:18px;font-size:13px}
.score b{font-size:20px;color:#1a8a66}
@page{margin:14mm}
`;

export interface FormPrintInput {
  title: string;
  /** "Client · Wednesday, September 23, 2026 · Submitted by …" (plain text). */
  meta: string;
  description?: string;
  /** The form's fields; empty or missing → a simple question / answer list. */
  fields?: LibField[];
  answers: Record<string, unknown>;
}

const answerText = (v: unknown) => (Array.isArray(v) ? v.join(', ') : String(v ?? '')).trim();

export function formPrintHtml(input: FormPrintInput): string {
  const answers: Record<string, string> = Object.fromEntries(
    Object.entries(input.answers).map(([k, v]) => [k, answerText(v)]),
  );
  const field = (label: string, value: string) =>
    `<div class="fld"><div class="lbl">${esc(label)}</div><div class="val${value ? '' : ' empty'}">${value ? esc(value) : 'No answer'}</div></div>`;

  let body = '';
  let scoreHtml = '';
  if (input.fields && input.fields.length) {
    const blocks = buildBlocks(input.fields);
    for (const b of blocks) {
      if (b.kind === 'heading') {
        body += `<h2>${esc(b.text)}</h2>`;
      } else if (b.kind === 'matrix') {
        if (b.title) body += `<h2>${esc(b.title)}</h2>`;
        const head = `<tr><th class="item"></th>${b.options.map((o) => `<th>${esc(o)}</th>`).join('')}</tr>`;
        const rows = b.items
          .map((it) => {
            const a = answers[it.id] ?? '';
            const cells = b.options
              .map((o) =>
                o === a
                  ? `<td class="on"><span class="dot on" aria-label="chosen"></span></td>`
                  : `<td><span class="dot"></span></td>`,
              )
              .join('');
            return `<tr><td class="item">${esc(it.label)}</td>${cells}</tr>`;
          })
          .join('');
        body += `<table><thead>${head}</thead><tbody>${rows}</tbody></table>`;
      } else {
        const f = b.field;
        if (f.type === 'heading' || f.type === 'section') body += `<h2>${esc(f.label)}</h2>`;
        else body += field(f.label || f.id, answers[f.id] ?? '');
      }
    }
    const s = blocksScore(blocks, answers);
    if (s.hasScore)
      scoreHtml = `<div class="score"><span>${esc(input.title)} (Scoring)</span><span><b>${s.score}</b> / ${s.scoreMax}</span></div>`;
  } else {
    body = Object.entries(answers)
      .map(([k, v]) => field(k, v))
      .join('');
  }

  return `<!doctype html><html><head><meta charset="utf-8"><title>${esc(input.title)}</title><style>${STYLES}</style></head><body><h1>${esc(input.title)}</h1><div class="meta">${esc(input.meta)}</div>${input.description ? `<div class="desc">${esc(input.description)}</div>` : ''}${body}${scoreHtml}</body></html>`;
}
